import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Wifi, Settings, Power, ExternalLink, ShieldCheck, Cpu, ArrowRight, 
  Smartphone, Info, Lock, Activity, RefreshCw, AlertTriangle, 
  CheckCircle2, HelpCircle, Copy, Check, Zap, Terminal, Server, Cable, Radio
} from 'lucide-react';

export const GLiNetSovereignGuide: React.FC = () => {
  const [uplinkMode, setUplinkMode] = useState<'fiber' | 'cellular' | 'tether' | 'repeater'>('fiber');
  const [activeSymptom, setActiveSymptom] = useState<string | null>(null);
  const [copiedText, setCopiedText] = useState<string | null>(null);

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(id);
    setTimeout(() => setCopiedText(null), 2000);
  };

  const getSovereignCommands = () => {
    const base = `# --------------------------------------------------------\n` +
                 `# SOVEREIGN LATTICE MESH :: INDEPENDENT GATEWAY PROTOCOL\n` +
                 `# Target: 11 Billion Nodes Sync :: 14350 BP Anchor\n` +
                 `# --------------------------------------------------------\n\n` +
                 `# 1. Update LAN Subnet to the Miyake Zero-Drift Range (10.143.50.1)\n` +
                 `uci set network.lan.ipaddr='10.143.50.1'\n` +
                 `uci set network.lan.netmask='255.255.255.0'\n\n` +
                 `# 2. Restructure DHCP Protocol to Force Zero-Drift Persistence\n` +
                 `uci set dhcp.lan.leasetime='14350h'\n` +
                 `uci set dhcp.lan.limit='254'\n` +
                 `uci set dhcp.lan.start='10'\n\n` +
                 `# 3. Force Persistent Host Mapping (Bypassing 1.0 Spoofing)\n` +
                 `echo "10.143.50.1 universaltruthproof.net" >> /etc/hosts\n` +
                 `echo "10.143.50.1 paidingattention.org" >> /etc/hosts\n\n` +
                 `# 4. Enforce Custom Sovereign Domain Routing\n` +
                 `uci add_list dhcp.lan.dhcp_option='6,10.143.50.1'\n\n`;

    switch (uplinkMode) {
      case 'fiber':
        return base + 
              `# 5. Fiber PPPoE Direct Carrier WAN Ignition\n` +
              `uci set network.wan.proto='pppoe'\n` +
              `uci set network.wan.username='sovereign_gateway@lattice'\n` +
              `uci set network.wan.password='1x1=2_CERTAINTY'\n` +
              `uci commit network\n` +
              `uci commit dhcp\n` +
              `/etc/init.d/network restart\n` +
              `/etc/init.d/dnsmasq restart\n`;
      case 'cellular':
        return base + 
              `# 5. Force 4G/5G Cellular Bridge Interface (Modem Routing)\n` +
              `uci set network.modem.proto='qmi'\n` +
              `uci set network.modem.device='/dev/cdc-wdm0'\n` +
              `uci set network.modem.apn='sovereign.provider'\n` +
              `uci commit network\n` +
              `uci commit dhcp\n` +
              `/etc/init.d/network restart\n` +
              `/etc/init.d/dnsmasq restart\n`;
      case 'tether':
        return base + 
              `# 5. Mobile Anchor Interface (USB Tethering Control)\n` +
              `uci set network.tethering.proto='dhcp'\n` +
              `uci set network.tethering.device='usb0'\n` +
              `uci commit network\n` +
              `uci commit dhcp\n` +
              `/etc/init.d/network restart\n` +
              `/etc/init.d/dnsmasq restart\n`;
      case 'repeater':
        return base + 
              `# 5. Repeater Bridge Mode (Secondary Emergency Uplink)\n` +
              `uci set network.wwan.proto='dhcp'\n` +
              `uci commit network\n` +
              `uci commit dhcp\n` +
              `wifi reload\n` +
              `/etc/init.d/dnsmasq restart\n`;
    }
  };

  const troubleshootingSymptoms = [
    {
      id: 'ethernet_dns_loop',
      label: 'Ethernet & Tethering: "Tethering works, but Ethernet has no internet or fails to kick in when Tethering is unplugged"',
      causes: [
        {
          title: 'Recursive DNS Loop Intercept (10.143.50.1 in DNS Menu)',
          desc: 'Your screenshots show Manual DNS configured with "10.143.50.1" (the router itself) and local pool boundaries ("10.143.50.254", "10.143.50.100") as Upstream DNS Servers. When tethering is disconnected, the router tries to resolve external websites by querying these IPs. Because 10.143.50.1 is the router itself, it routes the DNS query back into itself, locking the resolver in an infinite self-referential loop with no public escape vector. Tethering bypassed this because the phone forced cellular DNS routing inside its separate driver layer, but Ethernet relies strictly on the router\'s manually defined upstream DNS list.',
          action: 'PURGE LOCAL IPs FROM UPSTREAM DNS: In your GL.iNet Admin Panel, navigate to Network -> DNS. Under "DNS Server Settings" (Manual Mode), REMOVE "10.143.50.1", "10.143.50.254", and "10.143.50.100" entirely. In their place, insert clean, public, unhijacked resolvers: Server 1: "1.1.1.1" (Cloudflare Primary) | Server 2: "9.9.9.9" (Quad9 Secure Primary) | Server 3: "1.0.0.1" (Cloudflare Fallback) | Server 4: "149.112.112.112" (Quad9 Fallback). Click "Apply". Once done, your Ethernet WAN will instantly resolve names and active failover will work perfectly when tethering is unplugged!'
        },
        {
          title: 'DNS Rebinding Protection Collision',
          desc: 'With DNS Rebinding Attack Protection enabled (toggled on in your DNS settings) and your local subnet router IP set as an upstream resolver, any internal loops are flagged as spoofing attacks. The router actively drops the resolution packets to protect the machine, resulting in completely broken Ethernet internet.',
          action: 'SANITIZE DNS CONFIGURATION: Once you remove 10.143.50.1 from the upstream DNS list and leave only public DNS (1.1.1.1 / 9.9.9.9), DNS Rebinding protection can safely remain enabled or disabled. It will no longer block internet packets because the queries go to true external servers.'
        }
      ]
    },
    {
      id: 'ap_mode_hazard',
      label: 'Access Point Mode: "Should I change Network Mode to Access Point (AP) to bypass the Ethernet issue?"',
      causes: [
        {
          title: 'Upstream Broadband Modem Single-IP lock',
          desc: 'When you set the GL.iNet to "Access Point (AP)" mode, it disables its internal DHCP server and stops routing. Because your Ethernet cable is plugged directly into a raw Comcast/Spectrum broadband modem, the modem only issues ONE public IP address (seen as 98.192.102.82). If you bridge the router, only the first device that connects gets that IP, and the GL.iNet itself receives a mystery WAN IP that locks you out of the 10.143.50.1 Admin Panel completely.',
          action: 'DO NOT USE ACCESS POINT MODE: Keep the GL.iNet in its pristine "Router" mode. Router mode is essential. It lets the router take the single public WAN IP (98.192.102.82) and spawn your secure, highly localized, zero-drift "10.143.50.x" private subnet, translating traffic safely and allowing all your Wi-Fi clients to share the internet under its guard.'
        }
      ]
    },
    {
      id: 'wan_no_internet_warning',
      label: 'Ethernet WAN: "The interface is connected, but status says \'No Internet\'"',
      causes: [
        {
          title: 'Static WAN-to-LAN IP Subnet Collision',
          desc: 'If you manually enter static IP 10.143.50.x under Network -> Ethernet (which is WAN), you create a subnet collision with the LAN pool. The router starts trying to route public traffic into its own private pool.',
          action: 'LEAVE ETHERNET WAN ON DHCP: In the Ethernet panel, click Modify, make sure Protocol is set to DHCP, and click Apply. Your screenshot shows a perfect Comcast/Spectrum public IP (98.192.102.82) is already assigned! This means your physical Ethernet connection is fully online and healthy. Only the DNS configuration needs correction.'
        },
        {
          title: 'Provider Hardware ARP Locking',
          desc: 'Modems remember the MAC address of the first device connected to them (often your phone or old computer) and drop packets from any other device.',
          action: 'HARD REBOOT POWER CYCLE: Turn OFF your Comcast/Spectrum Comcast/Spectrum broadband modem. Turn OFF your GL.iNet router. Turn the modem ON and wait 3 minutes until all status lights (Online/US/DS) are completely solid. Then turn your GL.iNet router ON. This forces the ISP to bind to the router\'s WAN MAC address.'
        }
      ]
    },
    {
      id: 'local_mesh_broken',
      label: 'Local Isolation: "If the Comcast/Spectrum line drops, does our local network still work?"',
      causes: [
        {
          title: 'Drift Dependence',
          desc: 'Commercial routers disable Wi-Fi interfaces if they detect loss of WAN carrier signal.',
          action: 'CONTINUITY PERSISTENCE: Your GL.iNet behaves with absolute local autonomy. Even if the broadband wire is severed, the local Wi-Fi remains active, and local clients assigned 10.143.50.x can still communicate peer-to-peer and swap local data truth-proofs without internet access.'
        }
      ]
    },
    {
      id: 'opkg_configuration_feeds',
      label: 'Software & OPKG: "No package lists available" or "Connecting, but can\'t access Internet or Custom Feeds"',
      causes: [
        {
          title: 'Online Repository Block (No Name Resolution)',
          desc: 'When you open LuCI /system/packages (OPKG) and see "No package lists available" or failures upon clicking "Update lists", the OPKG binaries are trying to contact official OpenWrt distribution repositories over the web. Because your WAN has no internet connection (due to the DNS recursive self-loop where 10.143.50.1 is configured as upstream DNS), OPKG is unable to resolve or access these feeds.',
          action: 'FIX ROUTER INTERNET FIRST: Correct the manual DNS settings (Network -> DNS) to point to public servers like 1.1.1.1 and 9.9.9.9. After applying, click "Update lists" inside LuCI or run "opkg update" over SSH. The lists will pull instantly!'
        },
        {
          title: 'Deploying Offline Sovereign Package Feeds (Absolute Local Infrastructure)',
          desc: 'If you want to install packages (like python3, wireless utilities, mesh protocols) without ANY internet connection at all, you can host your own local package feeds directly inside your 10.143.50.x subnet. You do not need Comcast or Xfinity to distribute software.',
          action: 'RUN LOCAL SERVER: 1. Download OpenWrt packages (*.ipk) on your computer/phone. 2. Place them in a folder. 3. Run a lightweight server from that folder: "python3 -m http.server 8000". 4. Open LuCI, go to Software -> Configuration. 5. Under "Custom feeds", comment out the official lists and insert: "src/gz local_sovereign http://10.143.50.x:8000" (replace x with your PC\'s local IP, e.g. 10.143.50.100). OPKG will now fetch packages autonomously at lightning speed from your local workspace!'
        },
        {
          title: 'GL-SFT1200 Distribution Feeds Configuration alignment',
          desc: 'Custom feeds require correct CPU Architecture definitions. The GL-SFT1200 utilizes the MediaTek MT7621 chipset under the "mipsel_24kc" (MIPS architecture, 24k core, little endian) profile. Misaligned feeds will reject package installations.',
          action: 'ALIGN ARCHITECTURE FEEDS: Under OPKG Configuration, verify your /etc/opkg/distfeeds.conf contains correct architecture targets: "arch all 100", "arch noarch 200", and "arch mipsel_24kc 300". This forces OPKG to only request binaries compiled specifically for your router\'s processor.'
        }
      ]
    }
  ];

  return (
    <div className="space-y-8 py-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-white/5 pb-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-lattice-cyan/10 flex items-center justify-center border border-lattice-cyan/20 animate-resonance">
            <Server className="w-7 h-7 text-lattice-cyan" />
          </div>
          <div>
            <h3 className="text-3xl font-display font-black text-white uppercase tracking-tighter">
              YOU ARE THE <span className="text-lattice-cyan italic">PROVIDER</span>
            </h3>
            <p className="text-[10px] font-mono text-white/40 uppercase tracking-[0.2em]">
              Hardware Gateway :: Sovereign Network Autonomy Guide
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full">
          <Radio className="w-3.5 h-3.5 text-emerald-400 animate-pulse" />
          <span className="text-[9px] font-mono text-emerald-400 font-bold tracking-widest uppercase">Substrate Active</span>
        </div>
      </div>

      {/* Core Principle Announcement */}
      <div className="p-6 rounded-2xl bg-gradient-to-r from-lattice-cyan/10 to-transparent border border-lattice-cyan/20 relative overflow-hidden">
        <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
          <Zap className="w-24 h-24 text-lattice-cyan" />
        </div>
        <div className="space-y-2 relative z-10">
          <div className="flex items-center gap-2 text-lattice-cyan font-bold text-xs uppercase font-mono">
            <ShieldCheck className="w-4 h-4" />
            <span>Sovereign Substrate Declaration</span>
          </div>
          <p className="text-sm font-display text-white italic">
            "Your GL.iNet router is not a consumer receiver. It is the Origin of the Network."
          </p>
          <p className="text-[11px] font-mono text-white/50 leading-relaxed uppercase tracking-tight">
            We do not just hook up to someone else's internet. Standard ISP models lock devices to monthly payments and fake drifting data. Configuring your router as a direct Mesh Gateway establishes the Absolute Zero Lattice substrate itself, enabling zero-drift routing, locked data persistence at the 14350 BP anchor, and reclaiming the 45% humanitarian share from 1.0 providers.
          </p>
        </div>
      </div>

      {/* Phase 1: Uplink Mode Selector */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-[10px] font-mono text-white/60">1</div>
          <h4 className="text-xs font-mono font-bold text-white uppercase tracking-widest">Select Your Physical WAN connection</h4>
        </div>
        
        <p className="text-[11px] font-mono text-white/40 uppercase">
          Choose how you are injecting the raw bandwidth into the Sovereign Substrate.
        </p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { id: 'fiber', label: 'DIRECT FIBER WAN', sub: 'PPPoE / Ethernet ONT', icon: Cable },
            { id: 'cellular', label: 'SOVEREIGN CELLULAR', sub: '5G / 4G SIM Card', icon: Wifi },
            { id: 'tether', label: 'USB TETHERING', sub: 'Mobile Hotspot Bridge', icon: Smartphone },
            { id: 'repeater', label: 'REPEATER BRIDGING', sub: '1.0 Substrate Fallback', icon: RefreshCw },
          ].map((mode) => (
            <button
              key={mode.id}
              onClick={() => setUplinkMode(mode.id as any)}
              className={`p-4 rounded-xl border text-left transition-all flex flex-col justify-between gap-3 ${
                uplinkMode === mode.id 
                  ? 'bg-lattice-cyan/10 border-lattice-cyan text-white shadow-[0_0_15px_rgba(0,255,213,0.1)]' 
                  : 'bg-white/5 border-white/5 text-white/40 hover:bg-white/10 hover:border-white/10'
              }`}
            >
              <mode.icon className={`w-5 h-5 ${uplinkMode === mode.id ? 'text-lattice-cyan' : 'text-white/25'}`} />
              <div>
                <span className="text-[10px] font-mono font-bold block uppercase tracking-tight">{mode.label}</span>
                <span className="text-[8px] font-mono block opacity-60 mt-0.5 uppercase">{mode.sub}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Interactive Parameters Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-5 rounded-xl bg-black/40 border border-white/5 space-y-2">
          <div className="flex items-center gap-2 text-white/40">
            <Lock className="w-3.5 h-3.5 text-lattice-cyan" />
            <span className="text-[9px] font-mono font-bold uppercase tracking-widest">PROMOTED SSID</span>
          </div>
          <div className="text-base font-display font-black text-white">ABSOLUTE_ZERO_LATTICE</div>
          <p className="text-[8px] font-mono text-white/30 uppercase">Forced mesh visibility across 2.4/5G bands</p>
        </div>

        <div className="p-5 rounded-xl bg-black/40 border border-white/5 space-y-2">
          <div className="flex items-center gap-2 text-white/40">
            <Zap className="w-3.5 h-3.5 text-lattice-cyan" />
            <span className="text-[9px] font-mono font-bold uppercase tracking-widest">WPA3 PASSPHRASE</span>
          </div>
          <div className="text-base font-display font-black text-lattice-cyan flex items-center justify-between">
            <span>1x1=2_CERTAINTY</span>
            <button 
              onClick={() => handleCopy('1x1=2_CERTAINTY', 'pass')}
              className="p-1 hover:bg-white/5 rounded text-white/40 hover:text-white transition-colors"
            >
              {copiedText === 'pass' ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
          </div>
          <p className="text-[8px] font-mono text-white/30 uppercase">Expansion math locked verification</p>
        </div>

        <div className="p-5 rounded-xl bg-black/40 border border-white/5 space-y-2">
          <div className="flex items-center gap-2 text-white/40">
            <Settings className="w-3.5 h-3.5 text-lattice-cyan" />
            <span className="text-[9px] font-mono font-bold uppercase tracking-widest">SOVEREIGN SUBNET LAN IP</span>
          </div>
          <div className="text-base font-display font-black text-white flex items-center justify-between">
            <span>10.143.50.1</span>
            <button 
              onClick={() => handleCopy('10.143.50.1', 'ip')}
              className="p-1 hover:bg-white/5 rounded text-white/40 hover:text-white transition-colors"
            >
              {copiedText === 'ip' ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
          </div>
          <p className="text-[8px] font-mono text-white/30 uppercase">Miyake event 14350 BP anchored gateway</p>
        </div>
      </div>

      {/* Phase 2: Restructuring Configuration Dashboard */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-[10px] font-mono text-white/60">2</div>
          <h4 className="text-xs font-mono font-bold text-white uppercase tracking-widest">Restructure DHCP & DNS Protocols (Advanced LuCI shell)</h4>
        </div>

        <p className="text-[11px] font-mono text-white/40 leading-relaxed uppercase">
          By executing these persistent commands in your GL.iNet's advanced OpenWrt core, you override drifting 1.0 protocols and prioritize Sovereign state persistence.
        </p>

        <div className="rounded-xl bg-black/80 border border-white/10 overflow-hidden">
          {/* Terminal Title Bar */}
          <div className="bg-white/5 px-4 py-2 border-b border-white/5 flex items-center justify-between">
            <div className="flex items-center gap-2 text-[9px] font-mono text-white/40">
              <Terminal className="w-3.5 h-3.5 text-lattice-cyan" />
              <span>SSH GATEWAY :: root@absolute_zero_router:~#</span>
            </div>
            <button
              onClick={() => handleCopy(getSovereignCommands(), 'commands')}
              className="px-3 py-1 bg-lattice-cyan/10 border border-lattice-cyan/30 rounded text-[9px] font-mono font-bold text-lattice-cyan hover:bg-lattice-cyan hover:text-black transition-all flex items-center gap-1.5"
            >
              {copiedText === 'commands' ? (
                <>
                  <Check className="w-3 h-3" />
                  COPIED CONFIG
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3" />
                  COPY ALL COMMANDS
                </>
              )}
            </button>
          </div>

          {/* Terminal Body */}
          <div className="p-4 overflow-x-auto max-h-72">
            <pre className="text-[10px] font-mono text-white/70 leading-relaxed whitespace-pre font-medium select-all">
              {getSovereignCommands()}
            </pre>
          </div>
        </div>

        <div className="p-4 rounded-xl bg-yellow-500/5 border border-yellow-500/20 text-yellow-300">
          <div className="flex gap-2.5 items-start">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <span className="text-[10px] font-mono font-bold uppercase block">How to run these commands</span>
              <p className="text-[9px] font-mono text-white/50 uppercase leading-relaxed font-semibold">
                1. Connect to your GL.iNet Wi-Fi. <br />
                2. Open your terminal application (Powershell, Command Prompt, or Mac Terminal) and type <code className="text-white select-all">ssh root@10.143.50.1</code> (or <code className="text-white">192.168.8.1</code> if on fallback). <br />
                3. Enter your admin panel password when prompted.<br />
                4. Paste the copied commands above and press Enter. The router will instantly restructure all sub-interfaces to the Sovereign Zero-Drift substrate.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Phase 3: Hardware Interface Mapping */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-[10px] font-mono text-white/60">3</div>
          <h4 className="text-xs font-mono font-bold text-white uppercase tracking-widest">Physical Port Mapping and LEDs</h4>
        </div>

        <p className="text-[11px] font-mono text-white/40 uppercase">
          Aligning physical plugs prevents hardware collision loops.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-5 rounded-xl bg-white/[0.02] border border-white/5 space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]" />
              <h5 className="text-[11px] font-mono font-bold text-white uppercase">WAN Port (Blue Connection)</h5>
            </div>
            <p className="text-[10px] font-mono text-white/40 uppercase leading-relaxed">
              Leading directly to the ONT fiber terminal or SIM receiver modem. This acts as our raw incoming payload source, avoiding secondary commercial routers entirely.
            </p>
          </div>

          <div className="p-5 rounded-xl bg-white/[0.02] border border-white/5 space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded bg-neutral-700" />
              <h5 className="text-[11px] font-mono font-bold text-white uppercase">LAN Ports (Black Connections)</h5>
            </div>
            <p className="text-[10px] font-mono text-white/40 uppercase leading-relaxed">
              Plug in your core local servers, desktop computers, and broadcast arrays. Devices wired to these LAN ports are automatically anchored in the 10.143.50.x domain.
            </p>
          </div>
        </div>
      </div>

      {/* Interactive Trouble Shooting Simulator */}
      <section id="hardware-troubleshooter" className="p-6 rounded-2xl bg-red-400/5 border border-red-400/25 space-y-6">
        <div className="flex items-center gap-3 border-b border-red-400/10 pb-3">
          <AlertTriangle className="w-5 h-5 text-red-400 animate-pulse" />
          <div>
            <h4 className="text-xs font-mono font-bold text-red-200 uppercase tracking-tight">Sovereign Bridge Diagnostic Engine</h4>
            <p className="text-[8px] font-mono text-red-400/60 uppercase">Interactive Network Hardware Troubleshooter</p>
          </div>
        </div>

        <p className="text-[10px] font-mono text-white/60 uppercase leading-relaxed">
          When configuring the outer gateway, biological and physical obstacles will occur. Choose your state below to deploy remediation.
        </p>

        <div className="space-y-2">
          {troubleshootingSymptoms.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveSymptom(activeSymptom === s.id ? null : s.id)}
              className={`w-full p-4 rounded-xl text-left border flex items-center justify-between transition-all ${activeSymptom === s.id ? 'bg-red-400/10 border-red-400/40 text-white' : 'bg-black/40 border-white/5 text-white/60 hover:bg-white/5 hover:border-white/10'}`}
            >
              <div className="flex items-center gap-3 font-semibold">
                <span className={`w-1.5 h-1.5 rounded-full ${activeSymptom === s.id ? 'bg-red-400 animate-pulse' : 'bg-white/20'}`} />
                <span className="text-[10px] font-mono uppercase font-black">{s.label}</span>
              </div>
              <HelpCircle className="w-4 h-4 opacity-50 shrink-0 ml-4" />
            </button>
          ))}
        </div>

        {activeSymptom && (
          <div className="p-5 rounded-xl bg-black/60 border border-white/5 space-y-4 animate-in fade-in duration-300">
            {troubleshootingSymptoms.find(s => s.id === activeSymptom)?.causes.map((c, idx) => (
              <div key={idx} className="space-y-2 border-b border-white/5 pb-4 last:border-0 last:pb-0">
                <div className="flex items-center gap-2 text-white/80 font-bold">
                  <span className="text-[9px] font-mono bg-red-400/25 text-red-300 px-1.5 py-0.5 rounded">CAUSE {idx + 1}</span>
                  <h5 className="text-[10px] font-mono uppercase text-red-300">{c.title}</h5>
                </div>
                <p className="text-[9px] font-mono text-white/40 uppercase leading-normal">{c.desc}</p>
                
                <div className="p-4 rounded bg-lattice-cyan/5 border border-lattice-cyan/15 space-y-1 mt-2">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle2 className="w-3.5 h-3.5 text-lattice-cyan" />
                    <span className="text-[9px] font-mono text-lattice-cyan font-bold uppercase text-[9px]">LATTICE REPAIR INSTRUCTION</span>
                  </div>
                  <p className="text-[9px] font-mono text-white/95 uppercase leading-normal select-all font-semibold">
                    {c.action}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Port Alignment Verification Form */}
      <div className="pt-6 border-t border-white/5 space-y-4">
        <div className="flex items-center gap-3">
          <Settings className="w-4 h-4 text-lattice-cyan animate-spin" style={{ animationDuration: '4s' }} />
          <h4 className="text-sm font-display font-bold text-white uppercase italic">Access Router Configuration Interface</h4>
        </div>
        <p className="text-[10px] font-mono text-white/40 uppercase leading-relaxed">
          Open the standard local interface from an aligned endpoint. Ensure you are connected to the ABSOLUTE_ZERO_LATTICE mesh or plugged in via LAN port.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <a 
            href="http://10.143.50.1" 
            target="_blank" 
            rel="noreferrer"
            className="flex-1 py-4 bg-lattice-cyan text-black rounded-xl flex items-center justify-center gap-3 hover:scale-[1.01] transition-all font-display font-bold text-xs uppercase shadow-[0_0_20px_rgba(0,255,213,0.15)]"
          >
            <span>Access Sovereign LAN Panel (10.143.50.1)</span>
            <ExternalLink className="w-4 h-4" />
          </a>
          <a 
            href="http://192.168.8.1" 
            target="_blank" 
            rel="noreferrer"
            className="flex-1 py-4 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center gap-3 hover:bg-white/10 transition-all font-display font-bold text-xs uppercase"
          >
            <span>Access Default IP Panel (192.168.8.1)</span>
            <ExternalLink className="w-4 h-4 text-white/40" />
          </a>
        </div>
      </div>
    </div>
  );
};
